// Implements: F-UIX-008, F-UIX-010
/**
 * How long the login form waits after a `429`.
 *
 * `Retry-After` is read in its **delta-seconds** form only. The HTTP date form
 * is deliberately unsupported (design DD8): it needs a clock both ends agree
 * on, for something the backend does not emit today and will emit in seconds
 * when it does. Anything that is not a non-negative integer — absent header,
 * a date, `"soon"`, a negative number — falls back to the single constant
 * below, so there is exactly one number in the system to change.
 */

/**
 * The fallback wait, in seconds.
 *
 * Its source is the backend's own bucket window, written in
 * `backend/src/prototyper/identity/application/attempt_buckets.py` (read-only)
 * and quoted word for word in `TOO_MANY_ATTEMPTS`: "Vuelve a intentarlo en 30
 * segundos.". Showing a countdown that disagrees with the message the backend
 * just sent is worse than showing no countdown at all.
 */
export const RATE_LIMIT_WAIT_SECONDS = 30;

/** Only digits, so `"30.5"`, `"+30"`, `"30 "` and a date are all rejected. */
const DELTA_SECONDS_RE = /^\d+$/;

/**
 * The seconds to wait, from the `Retry-After` header of a `429`.
 *
 * @param header The raw header value, or `null` when the response has none.
 * @returns The parsed delta-seconds, or `RATE_LIMIT_WAIT_SECONDS`.
 */
export function parseRetryAfter(header: string | null | undefined): number {
  if (header === null || header === undefined) return RATE_LIMIT_WAIT_SECONDS;
  if (!DELTA_SECONDS_RE.test(header)) return RATE_LIMIT_WAIT_SECONDS;

  const seconds = Number.parseInt(header, 10);
  // `0` is a valid delta-seconds and means "retry now", but a countdown of
  // zero is indistinguishable from no countdown, so it takes the constant too.
  return seconds > 0 ? seconds : RATE_LIMIT_WAIT_SECONDS;
}
